import { CourseContentType } from '@/client/courses';
import {
  QuestionChange,
  SlideChange,
} from '@/client/courses/types/course-changes.type';
import {
  getTextDifferences,
  normalizePunctuationForComparison,
} from '@/components/text-highlighting/utils/highlighting.utils';

// Helper function to check if two text values are different
// Punctuation-only differences (curly vs straight quotes/apostrophes) are
// normalized away so they do not inflate the change count, matching the
// backend change detection.
export const hasTextChanged = (
  original: string | null | undefined,
  customized: string | null | undefined,
): boolean => {
  // If both are empty/null, no change
  if (!original && !customized) return false;

  // If one is empty but the other isn't, it's a change
  if (!original || !customized) return true;

  const segments = getTextDifferences(
    normalizePunctuationForComparison(original),
    normalizePunctuationForComparison(customized),
  );
  return segments.some((segment) => segment.isChanged);
};

// Helper function to check if two arrays are different
export const hasArrayChanged = (
  original: string[] | boolean[] | null | undefined,
  customized: string[] | boolean[] | null | undefined,
): boolean => {
  // If both are empty/null, no change
  if (!original && !customized) return false;

  // If one is empty but the other isn't, it's a change
  if (!original || !customized) return true;

  // Compare arrays as JSON strings
  return JSON.stringify(original) !== JSON.stringify(customized);
};

// Count the number of changes in a slide
export const countSlideChanges = (slide: SlideChange): number => {
  let count = 0;

  if (hasTextChanged(slide.originalHeadline, slide.customizedHeadline)) count++;
  if (hasTextChanged(slide.originalContent, slide.customizedContent)) count++;
  if (hasTextChanged(slide.originalFootnote, slide.customizedFootnote)) count++;
  if (hasTextChanged(slide.originalInfobox, slide.customizedInfobox)) count++;

  if (slide.imageTextChanges) {
    for (const imgChange of slide.imageTextChanges) {
      if (
        hasTextChanged(imgChange.originalTitle, imgChange.customizedTitle) ||
        hasTextChanged(
          imgChange.originalDescription,
          imgChange.customizedDescription,
        )
      ) {
        count++;
      }
    }
  }

  const interactiveElementChange = slide.interactiveElementChange;
  if (interactiveElementChange) {
    if (
      hasTextChanged(
        interactiveElementChange.originalSender,
        interactiveElementChange.customizedSender,
      )
    )
      count++;
    if (
      hasTextChanged(
        interactiveElementChange.originalMessage,
        interactiveElementChange.customizedMessage,
      )
    )
      count++;
    if (
      hasTextChanged(
        interactiveElementChange.originalHeader,
        interactiveElementChange.customizedHeader,
      )
    )
      count++;
    if (
      hasTextChanged(
        interactiveElementChange.originalBody,
        interactiveElementChange.customizedBody,
      )
    )
      count++;
  }

  return count;
};

// Count the number of changes in a question
export const countQuestionChanges = (question: QuestionChange): number => {
  let count = 0;

  if (hasTextChanged(question.originalText, question.customizedText)) count++;
  if (hasArrayChanged(question.originalAnswers, question.customizedAnswers))
    count++;
  if (
    hasTextChanged(question.originalExplanation, question.customizedExplanation)
  )
    count++;
  if (
    hasArrayChanged(
      question.originalCorrectAnswers,
      question.customizedCorrectAnswers,
    )
  )
    count++;

  const questionInteractiveElementChange = question.interactiveElementChange;
  if (questionInteractiveElementChange) {
    if (
      hasTextChanged(
        questionInteractiveElementChange.originalSender,
        questionInteractiveElementChange.customizedSender,
      )
    )
      count++;
    if (
      hasTextChanged(
        questionInteractiveElementChange.originalMessage,
        questionInteractiveElementChange.customizedMessage,
      )
    )
      count++;
    if (
      hasTextChanged(
        questionInteractiveElementChange.originalHeader,
        questionInteractiveElementChange.customizedHeader,
      )
    )
      count++;
    if (
      hasTextChanged(
        questionInteractiveElementChange.originalBody,
        questionInteractiveElementChange.customizedBody,
      )
    )
      count++;
  }

  return count;
};

export const getContentTypeName = (type: CourseContentType, t: any): string => {
  switch (type) {
    case CourseContentType.PRESENTATION:
      return t('content.type.presentation');
    case CourseContentType.QUIZ:
      return t('content.type.quiz');
    case CourseContentType.RESOURCE:
      return t('content.type.resource');
    default:
      return t('content.type.unknown');
  }
};
